/**
 * Today's Review Tab
 * Shows the due-today queue (vocab + KP + pattern) plus memory health snapshot,
 * with one-click entry into the practice session (top-30 most-fading items).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import learningJourneyService, {
  DailyMenu,
  MemoryHealth,
} from '../../../services/learningJourneyService';
import './TodayTab.css';

const SESSION_CAP = 30;

const rColor = (r: number) => (r >= 0.9 ? '#16a34a' : r >= 0.7 ? '#f59e0b' : '#dc2626');

const buildPracticeHref = (menu: DailyMenu): string => {
  const top = menu.reviewItems.slice(0, SESSION_CAP);
  const params = new URLSearchParams();
  const vocabIds = top.filter(i => i.type === 'vocabulary').map(i => i.itemId);
  const kpIds = top.filter(i => i.type === 'knowledgePoint').map(i => i.itemId);
  const patternIds = top.filter(i => i.type === 'pattern').map(i => i.itemId);
  if (vocabIds.length) params.set('vocabIds', vocabIds.join(','));
  if (kpIds.length) params.set('kpIds', kpIds.join(','));
  if (patternIds.length) params.set('patternIds', patternIds.join(','));
  params.set('mode', 'smart');
  params.set('count', String(SESSION_CAP));
  params.set('stages', 'speaking,dictation,meaning,fillBlank');
  return `/vocabulary-book/practice?${params.toString()}`;
};

const TodayTab: React.FC = () => {
  const { t } = useTranslation('review');
  const [menu, setMenu] = useState<DailyMenu | null>(null);
  const [health, setHealth] = useState<MemoryHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      learningJourneyService.getDailyMenu(),
      learningJourneyService.getMemoryHealth(),
    ])
      .then(([menuRes, healthRes]) => {
        if (cancelled) return;
        if (menuRes.status === 'fulfilled') setMenu(menuRes.value);
        if (healthRes.status === 'fulfilled') setHealth(healthRes.value);
        if (menuRes.status === 'rejected' && healthRes.status === 'rejected') {
          setError(t('today.loadError'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (loading) {
    return (
      <div className="rc-loading">
        <div className="rc-spinner" aria-hidden="true" />
        <p>{t('today.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rc-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="rc-today">
      {/* Daily Menu Card */}
      {menu && (
        <div className="rc-today__menu">
          {menu.reviewCount > 0 ? (
            <DailyMenuView menu={menu} t={t} />
          ) : (
            <div className="rc-tab-empty">
              <p>{t('today.allClear')}</p>
              <p className="rc-today__sub">{t('today.allClearSub')}</p>
            </div>
          )}
        </div>
      )}

      {/* Memory Health */}
      {health && health.total > 0 && <MemoryHealthView health={health} t={t} />}
    </div>
  );
};

/* ── Daily Menu sub-view ──────────────────────────── */
const DailyMenuView: React.FC<{
  menu: DailyMenu;
  t: (key: string, opts?: any) => string;
}> = ({ menu, t }) => {
  const vocabItems = menu.reviewItems.filter(i => i.type === 'vocabulary');
  const kpItems = menu.reviewItems.filter(i => i.type === 'knowledgePoint');
  const patternItems = menu.reviewItems.filter(i => i.type === 'pattern');

  return (
    <div className="rc-card">
      <div className="rc-card__header">
        <h2 className="rc-card__title">{t('today.queueTitle')}</h2>
        <span className="rc-card__badge">
          {t('today.queueBadge', { count: menu.reviewCount, minutes: menu.estimatedMinutes })}
        </span>
      </div>

      {menu.streakDay > 0 && (
        <div className="rc-today__streak">{t('today.streak', { days: menu.streakDay })}</div>
      )}

      <div className="rc-today__groups">
        {vocabItems.length > 0 && (
          <ItemGroup
            label={t('today.vocabGroup')}
            colorClass="rc-type-vocab"
            items={vocabItems}
            t={t}
          />
        )}
        {kpItems.length > 0 && (
          <ItemGroup
            label={t('today.kpGroup')}
            colorClass="rc-type-kp"
            items={kpItems}
            t={t}
          />
        )}
        {patternItems.length > 0 && (
          <ItemGroup
            label={t('today.patternGroup')}
            colorClass="rc-type-pattern"
            items={patternItems}
            t={t}
          />
        )}
      </div>

      <Link to={buildPracticeHref(menu)} className="rc-today__cta">
        {t('today.startCta', { count: Math.min(menu.reviewCount, SESSION_CAP) })}
      </Link>

      {menu.reviewCount > SESSION_CAP && (
        <p className="rc-today__cap-hint">
          {t('today.capHint', { cap: SESSION_CAP, total: menu.reviewCount })}
        </p>
      )}
    </div>
  );
};

const ItemGroup: React.FC<{
  label: string;
  colorClass: string;
  items: { label: string; retrievability?: number }[];
  t: (key: string) => string;
}> = ({ label, colorClass, items, t }) => {
  const [open, setOpen] = useState(false);
  const rLabel = (rv: number) =>
    rv >= 0.9 ? t('retrievability.solid') : rv >= 0.7 ? t('retrievability.needReview') : t('retrievability.fading');

  return (
    <div className="rc-group">
      <button className="rc-group__header" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className={`rc-type-tag ${colorClass}`}>{label} {items.length}</span>
        {!open && (
          <span className="rc-group__preview">
            {items.slice(0, 3).map(i => i.label).join(', ')}
            {items.length > 3 && ` ...+${items.length - 3}`}
          </span>
        )}
        <span className={`rc-group__chevron ${open ? 'rc-group__chevron--open' : ''}`} aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 5 6 8 9 5" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="rc-group__items">
          {items.map((item, i) => (
            <div key={i} className="rc-group__row">
              <span className="rc-group__label">{item.label}</span>
              {item.retrievability !== undefined && (
                <span className="rc-group__r" style={{ color: rColor(item.retrievability) }}>
                  {rLabel(item.retrievability)} {(item.retrievability * 100).toFixed(0)}%
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Memory Health sub-view ──────────────────────── */
const MemoryHealthView: React.FC<{
  health: MemoryHealth;
  t: (key: string, opts?: any) => string;
}> = ({ health, t }) => {
  const total = health.total || 1;
  const solidPct = (health.solid / total) * 100;
  const reviewPct = (health.needReview / total) * 100;
  const fadingPct = (health.fading / total) * 100;

  return (
    <div className="rc-card rc-health">
      <div className="rc-card__header">
        <h2 className="rc-card__title">{t('today.healthTitle')}</h2>
        <span className="rc-card__badge rc-card__badge--muted">
          {t('today.healthTotal', { count: health.total })}
        </span>
      </div>

      <div className="rc-health__bar" aria-hidden="true">
        <div className="rc-health__seg rc-health__seg--solid" style={{ width: `${solidPct}%` }} />
        <div className="rc-health__seg rc-health__seg--review" style={{ width: `${reviewPct}%` }} />
        <div className="rc-health__seg rc-health__seg--fading" style={{ width: `${fadingPct}%` }} />
      </div>

      <div className="rc-health__legend">
        <span className="rc-health__stat">
          <span className="rc-dot rc-dot--solid" />
          {t('today.healthSolid', { count: health.solid })}
        </span>
        <span className="rc-health__stat">
          <span className="rc-dot rc-dot--review" />
          {t('today.healthReview', { count: health.needReview })}
        </span>
        <span className="rc-health__stat">
          <span className="rc-dot rc-dot--fading" />
          {t('today.healthFading', { count: health.fading })}
        </span>
      </div>

      {health.tomorrowProjection.newFading > 0 && (
        <p className="rc-health__projection">
          {t('today.tomorrowWarning', { count: health.tomorrowProjection.newFading })}
        </p>
      )}
    </div>
  );
};

export default TodayTab;
